<template>
	<button
		v-if="hasNoMembers"
		v-tooltip="{
			content: isDropdownEnabled ? noMembersTooltip : '',
			popperClass: `user-assign-select-assignee-tooltip`,
		}"
		type="button"
		:disabled="!isDropdownEnabled"
		:aria-label="noMembersTooltip"
		class="user-assign-select__round-icon-wrapper"
		@click="$emit('click')"
		@dblclick="$emit('dblclick')"
		@keypress="$emit('click')"
	>
		<SuiIcon
			class="user-assign-select__round-icon"
			name="icon-addperson-16"
		/>
	</button>
	<button
		v-else
		type="button"
		:disabled="!isDropdownEnabled"
		class="user-assign-select__avatar-wrapper"
		@click="$emit('click')"
		@dblclick="$emit('dblclick')"
	>
		<div
			v-for="role in assignedRoles"
			:key="role.id"
			v-tooltip="{
				content: role.name,
				popperClass: `user-assign-select-assignee-tooltip`,
			}"
			class="user-assign-select__avatar"
		>
			<SuiAvatar
				:aria-label="role.name"
				:size="avatarSize"
				:initials="role.initials || getInitials(role.name)"
				:color="role.hslColor ?? ''"
				show-border
				variant="dashed"
				class="user-assign-select__avatar-role"
			/>

			<template v-if="size === UserAssignSelectSize.LARGE">
				<div class="user-assign-select__avatar-name">
					{{ role.name }}
				</div>

				<div
					v-if="confirmedAllocatedTimes?.roles.get(role.id)"
					class="user-assign-select__avatar-time"
				>
					{{ formatRoleAllocatedTime(role.id) }}
				</div>
			</template>
		</div>

		<div
			v-for="member in assignedUsersWithAvatars"
			:key="member.id"
			v-tooltip="{
				content: getUserTooltip(member),
				popperClass: `user-assign-select-assignee-tooltip`,
			}"
			class="user-assign-select__avatar"
		>
			<SuiAvatar
				:aria-label="member.name"
				:is-external-user="member.type === UserType.Cupo"
				:url="getUserProfileImage(member, size)"
				:size="avatarSize"
				:initials="member.initials ?? ''"
				:color="member.hslColor ?? ''"
				show-border
			/>

			<template v-if="size === UserAssignSelectSize.LARGE">
				<div class="user-assign-select__avatar-name">
					{{ member.firstName }}
				</div>

				<div
					v-if="confirmedAllocatedTimes?.users.get(member.id)"
					class="user-assign-select__avatar-time"
				>
					{{ formatUserAllocatedTime(member.id) }}
				</div>
			</template>
		</div>

		<SuiAvatar
			v-if="isExtraCountShown"
			class="user-assign-select__avatar"
			:aria-label="$t('user_assign_extra_label')"
			:size="avatarSize"
			:initials="`+${hiddenCount}`"
			:color="Constants.Colors.INACTIVE_COLOR"
			show-border
		/>
	</button>
</template>

<script setup lang="ts">
import { SuiAvatar, SuiIcon } from '@developers/scoro-ui';
import {
	type UserAssignSelectUserFragment,
	UserType,
} from '@/generated/graphql';
import { UserAssignSelectSize } from '../../constants';
import { type ValueOf } from '@/types';
import { type TaskAssigneeAllocatedDuration } from '@/features/Task/components/TaskUsers/types';
import { Constants } from '@/constants';
import {
	type UserAssignAvatarSize,
	type UserAssignSelectRole,
} from '../../types';
import { getInitials, getUserTooltip, getUserProfileImage } from '../../utils';
import { secondsToHoursAndMinutes } from '@/utils/time';

export interface UserAssignSelectTriggerProps {
	hasNoMembers: boolean;
	noMembersTooltip: string;
	isDropdownEnabled: boolean;
	assignedUsersWithAvatars: UserAssignSelectUserFragment[];
	assignedRoles?: UserAssignSelectRole[];
	isExtraCountShown: boolean;
	hiddenCount: number;
	avatarSize: UserAssignAvatarSize;
	size: ValueOf<typeof UserAssignSelectSize>;
	confirmedAllocatedTimes?: TaskAssigneeAllocatedDuration;
}

const {
	assignedRoles = [],
	size,
	confirmedAllocatedTimes,
} = defineProps<UserAssignSelectTriggerProps>();

defineEmits<{
	click: [];
	dblclick: [];
}>();

const formatUserAllocatedTime = (userId: string): string => {
	const time = confirmedAllocatedTimes?.users.get(userId);
	return time ? secondsToHoursAndMinutes(time) : '';
};

const formatRoleAllocatedTime = (roleId: string): string => {
	const time = confirmedAllocatedTimes?.roles.get(roleId);
	return time ? secondsToHoursAndMinutes(time) : '';
};
</script>

<style lang="scss" scoped>
.user-assign-select__avatar-role {
	--sui-avatar-dashed-background-color: #{$neutral-00};
}
</style>
